// client/src/components/BidNoBidSidebar.jsx
// Rendered in TenderModal stage 0 when tender is awaiting a bid/no-bid decision.
// Visible only to Manager and Director roles. Uses POST /api/tenders/:id/bid-decision
// which enforces authority limits and records the decision in bid_approvals.
//
// Lockout: if scope/estimator/site address+W3W (for construction) are missing,
// the decision controls are hidden and a lockout panel explains what's needed.
// Backend also enforces this — frontend gate is UX, backend gate is integrity.

import { useState } from 'react';
import { AlertTriangle, CheckCircle, XCircle, Lock } from 'lucide-react';
import { api } from '../api';

const SCOPE_MIN_CHARS = 20;

function missingRequiredFields(tender) {
  const missing = [];

  const scope = (tender?.initial_notes || '').trim();
  if (scope.length < SCOPE_MIN_CHARS) {
    missing.push(`Scope of Works (at least ${SCOPE_MIN_CHARS} characters)`);
  }

  if (!tender?.estimator_user_id) {
    missing.push('Assigned Estimator');
  }

  // SUR Phase 1: address is required for construction AND surveying;
  // W3W is required only for construction.
  const requiresAddress = ['construction', 'surveying'].includes(tender?.contract_type);
  const requiresW3W     = tender?.contract_type === 'construction';
  if (requiresAddress) {
    if (!tender?.site_address || !tender.site_address.trim()) {
      missing.push('Site Address');
    }
  }
  if (requiresW3W) {
    const w3w = (tender?.what3words || '').trim();
    if (!w3w || !/^[a-z]+\.[a-z]+\.[a-z]+$/.test(w3w)) {
      missing.push('What3Words (word.word.word)');
    }
  }

  return missing;
}

export default function BidNoBidSidebar({ tender, user, onDecided }) {
  const [decision, setDecision] = useState(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const role = user?.role;
  const isManagerOrDirector = role === 'manager' || role === 'director';
  const isAwaitingDecision = tender?.status === 'Awaiting Decision';

  if (!isAwaitingDecision || !isManagerOrDirector) return null;
  if (!tender?.id) return null;

  const missing = missingRequiredFields(tender);
  const isLocked = missing.length > 0 && !result;

  const tenderValuePence = Math.round((tender.price || 0) * 100);
  const authPence = user?.bid_authority_pence || 0;
  const isDirector = role === 'director';
  const withinAuthority = isDirector || (authPence > 0 && tenderValuePence <= authPence);
  const willRecommend = !isDirector && !withinAuthority && decision === 'bid';

  const canSubmit = decision !== null
    && !(decision === 'no_bid' && !notes.trim())
    && !loading
    && !isLocked;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setError('');
    try {
      const payload = { decision };
      if (notes.trim()) payload.notes = notes.trim();
      const updated = await api.bidDecision(tender.id, payload);
      setResult(updated);
      if (onDecided) onDecided(updated);
    } catch (e) {
      setError(e?.message || 'Failed to submit decision');
    } finally {
      setLoading(false);
    }
  };

  const panelStyle = {
    borderLeft: '1px solid var(--border)',
    paddingLeft: 18,
    minWidth: 280,
    maxWidth: 320,
  };
  const labelStyle = { display: 'block', color: 'var(--text-dim)', fontSize: 12, marginBottom: 4 };
  const textareaStyle = {
    width: '100%', padding: '9px 12px',
    background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: 8,
    color: 'var(--text)', fontSize: 13, boxSizing: 'border-box',
    minHeight: 78, resize: 'vertical', fontFamily: 'inherit',
  };
  const pillStyle = (active, tone) => ({
    flex: 1,
    padding: '10px 12px',
    background: active ? (tone === 'approve' ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)')
                       : 'rgba(255,255,255,0.04)',
    border: `1px solid ${active
      ? (tone === 'approve' ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.4)')
      : 'var(--border)'}`,
    color: active ? (tone === 'approve' ? '#10b981' : '#f87171') : 'var(--text-dim)',
    borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: active ? 600 : 500,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  });
  const submitStyle = {
    width: '100%', padding: '10px 12px',
    background: canSubmit
      ? (decision === 'no_bid' ? 'rgba(239,68,68,0.2)' : 'rgba(16,185,129,0.2)')
      : 'var(--surface2)',
    border: `1px solid ${canSubmit
      ? (decision === 'no_bid' ? 'rgba(239,68,68,0.4)' : 'rgba(16,185,129,0.4)')
      : 'var(--border)'}`,
    color: canSubmit
      ? (decision === 'no_bid' ? '#f87171' : '#10b981')
      : 'var(--text-dim)',
    borderRadius: 8, fontWeight: 600, fontSize: 13,
    cursor: canSubmit ? 'pointer' : 'not-allowed',
    marginTop: 12,
  };

  if (result) {
    const isApproved = result.status === 'Bidding';
    const stayedInReview = result.status === 'Awaiting Decision';
    const isDeclined = result.status === 'No Bid' || result.status === 'no_bid';
    return (
      <div style={panelStyle}>
        <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
          Decision recorded
        </p>
        {isApproved && (
          <div style={{
            padding: '10px 12px',
            background: 'rgba(16,185,129,0.1)',
            border: '1px solid rgba(16,185,129,0.25)',
            borderRadius: 8, color: 'var(--text)', fontSize: 12, lineHeight: 1.5,
          }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', color: '#10b981', fontWeight: 600, marginBottom: 4 }}>
              <CheckCircle size={14} /> Approved for bidding
            </div>
            Reference updated to <strong>{result.project_ref || result.est_reference}</strong>.
            The estimator and commercial team have been notified.
          </div>
        )}
        {stayedInReview && (
          <div style={{
            padding: '10px 12px',
            background: 'rgba(245,158,11,0.1)',
            border: '1px solid rgba(245,158,11,0.25)',
            borderRadius: 8, color: 'var(--text)', fontSize: 12, lineHeight: 1.5,
          }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', color: '#f59e0b', fontWeight: 600, marginBottom: 4 }}>
              <AlertTriangle size={14} /> Recommendation sent
            </div>
            Tender value exceeds your authority limit. Director has been notified for final decision.
          </div>
        )}
        {isDeclined && (
          <div style={{
            padding: '10px 12px',
            background: 'rgba(239,68,68,0.1)',
            border: '1px solid rgba(239,68,68,0.25)',
            borderRadius: 8, color: 'var(--text)', fontSize: 12, lineHeight: 1.5,
          }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', color: '#ef4444', fontWeight: 600, marginBottom: 4 }}>
              <XCircle size={14} /> Declined
            </div>
            Tender moved to No Bid. Reason recorded in audit log.
          </div>
        )}
      </div>
    );
  }

  if (isLocked) {
    return (
      <div style={panelStyle}>
        <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
          Bid Decision
        </p>
        <div style={{
          padding: '12px 14px',
          background: 'rgba(245,158,11,0.1)',
          border: '1px solid rgba(245,158,11,0.3)',
          borderRadius: 8,
          fontSize: 12, color: 'var(--text)', lineHeight: 1.5,
        }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', color: '#f59e0b', fontWeight: 600, marginBottom: 8 }}>
            <Lock size={14} /> Information required
          </div>
          <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--text-dim)' }}>
            A bid/no-bid decision cannot be made until the following are completed on this tab:
          </p>
          <ul style={{ margin: '0 0 0 16px', padding: 0, fontSize: 12, color: 'var(--text)', lineHeight: 1.6 }}>
            {missing.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
          <p style={{ margin: '10px 0 0', fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>
            Save the tender once these fields are completed; the decision controls will then become available.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={panelStyle}>
      <p style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
        Bid Decision
      </p>

      <div style={{
        padding: '10px 12px',
        background: 'rgba(217,119,6,0.08)',
        border: '1px solid rgba(217,119,6,0.2)',
        borderRadius: 8,
        marginBottom: 14,
        fontSize: 12, color: 'var(--text-dim)', lineHeight: 1.5,
      }}>
        Direct enquiry awaiting bid/no-bid decision (ISO).
        {!isDirector && authPence > 0 && (
          <div style={{ marginTop: 6, fontSize: 11 }}>
            Your authority limit: <strong>£{(authPence / 100).toLocaleString('en-GB')}</strong>
          </div>
        )}
      </div>

      <label style={labelStyle}>Decision</label>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        <button
          type="button"
          onClick={() => { setDecision('bid'); setError(''); }}
          style={pillStyle(decision === 'bid', 'approve')}
          disabled={loading}
        >
          <CheckCircle size={14} /> Bid
        </button>
        <button
          type="button"
          onClick={() => { setDecision('no_bid'); setError(''); }}
          style={pillStyle(decision === 'no_bid', 'decline')}
          disabled={loading}
        >
          <XCircle size={14} /> No Bid
        </button>
      </div>

      {willRecommend && (
        <div style={{
          padding: '8px 10px',
          background: 'rgba(245,158,11,0.08)',
          border: '1px solid rgba(245,158,11,0.2)',
          borderRadius: 7,
          marginBottom: 12,
          fontSize: 11, color: '#f59e0b', lineHeight: 1.4,
          display: 'flex', gap: 6, alignItems: 'flex-start',
        }}>
          <AlertTriangle size={12} style={{ marginTop: 2, flexShrink: 0 }} />
          <span>Tender value exceeds your authority. This will be sent to the Director as a recommendation.</span>
        </div>
      )}

      <label style={labelStyle}>
        {decision === 'no_bid' ? 'Reason' : 'Notes'}
        {decision === 'no_bid' && <span style={{ color: '#ef4444' }}> *</span>}
        {decision !== 'no_bid' && <span style={{ color: 'var(--text-muted)' }}> (optional)</span>}
      </label>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder={decision === 'no_bid'
          ? 'Why are we declining? (recorded in audit log)'
          : 'Optional context for the decision.'}
        style={textareaStyle}
        disabled={loading}
      />

      {error && (
        <div style={{
          marginTop: 10, padding: '8px 10px',
          background: 'rgba(239,68,68,0.1)',
          border: '1px solid rgba(239,68,68,0.25)',
          borderRadius: 7, color: '#f87171', fontSize: 11, lineHeight: 1.4,
          display: 'flex', gap: 6, alignItems: 'flex-start',
        }}>
          <AlertTriangle size={12} style={{ marginTop: 2, flexShrink: 0 }} />
          <span>{error}</span>
        </div>
      )}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={!canSubmit}
        style={submitStyle}
      >
        {loading ? 'Submitting…' : decision === 'no_bid'
          ? 'Confirm No Bid'
          : willRecommend ? 'Send Recommendation' : 'Approve for Bidding'}
      </button>
    </div>
  );
}
